import type { UserPlan } from "@/types";
import i18n from "@/i18n";

/**
 * Client mirror of the server's reflection plan limits (kept in sync with
 * backend/api/src/modules/reflections/reflection-limits.ts). Used to size inputs
 * and frame the paywall copy before the quota call returns.
 */
export type ReflectionLimits = {
  /** Max reflections per calendar month. `null` means unlimited (Plus). */
  perMonth: number | null;
  noteMaxLength: number;
  canExport: boolean;
};

// There is no history window on either plan: a reflection is kept until the
// person deletes it, so nothing here bounds how far back the journal goes.
const FREE: ReflectionLimits = {
  perMonth: 3,
  noteMaxLength: 200,
  canExport: false,
};

const PLUS: ReflectionLimits = {
  perMonth: null, // unlimited
  noteMaxLength: 1000,
  canExport: true,
};

export function reflectionLimits(plan: UserPlan | undefined): ReflectionLimits {
  return plan === "plus" ? PLUS : FREE;
}

/** Framing copy for the reflection quota (varies by plan). */
export function reflectionQuotaIntro(plan: UserPlan | undefined): string {
  return i18n.t(
    plan === "plus"
      ? "growth.dashboard.quotaIntroPlus"
      : "growth.dashboard.quotaIntroFree",
  );
}

/** The "you're out for today" message — upgrade nudge only for free users. */
export function reflectionLimitReachedCopy(plan: UserPlan | undefined): {
  title: string;
  body: string;
} {
  const isPlus = plan === "plus";
  return {
    title: i18n.t(
      isPlus
        ? "growth.reflectionFlow.limitTitlePlus"
        : "growth.reflectionFlow.limitTitleFree",
      {
        // Only Free hits this cap (Plus is unlimited); fall back defensively.
        count: (isPlus ? PLUS.perMonth : FREE.perMonth) ?? 0,
      },
    ),
    body: i18n.t(
      isPlus
        ? "growth.reflectionFlow.limitBodyPlus"
        : "growth.dashboard.plusGate.reflectionsBody",
    ),
  };
}
